import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';

export interface ServiceOrderWorkSection {
  id: number;
  serviceOrderId: number | null;
  description: string;
  status?: string | null;
  sourceBudgetWorkSectionId?: number | null;
  totalApproved?: number | null;
  approved?: boolean | null;
  approvedBy?: string | null;
  externalServiceProviderId?: number | null;
  serviceOrderStockRequestsId?: number | null;
  externalServiceProvider?: ServiceOrderWorkSectionExternalProvider | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  items?: ServiceOrderWorkSectionItem[];
  services?: ServiceOrderWorkSectionService[];
}

export interface ServiceOrderWorkSectionExternalProvider {
  id?: number | null;
  tradeName?: string | null;
  companyName?: string | null;
  cnpj?: string | null;
  phone?: string | null;
  contactEmail?: string | null;
  responsibleName?: string | null;
  serviceType?: string | null;
}

export interface ServiceOrderWorkSectionCreate {
  serviceOrderId: number;
  description: string;
  status?: string;
  externalServiceProviderId?: number | null;
}

export interface ServiceOrderWorkSectionUpdate {
  serviceOrderId?: number;
  description?: string;
  status?: string;
  externalServiceProviderId?: number | null;
  serviceOrderStockRequestsId?: number | null;
}

/** Normaliza o id retornado pelo POST de service-order-stock-requests (escopo OS). */
export function extractServiceOrderStockRequestId(response: unknown): number | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const root = response as Record<string, unknown>;
  const data = (root['data'] ?? root['Data'] ?? root) as Record<string, unknown>;
  const candidates = [
    data['serviceOrderStockRequestId'],
    data['ServiceOrderStockRequestId'],
    data['ServiceOrderStockRequestsID'],
    data['serviceOrderStockRequestsId'],
    data['id'],
    data['Id']
  ];

  for (const value of candidates) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

/** Normaliza chave de status para comparação (case/acentos/separadores). */
export function normalizeWorkSectionStatusKey(status: string | null | undefined): string {
  return String(status ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Mapeia status da solicitação de estoque para status da Work Section (OS normal). */
export function mapStockRequestStatusToWorkSectionStatus(stockStatus: string | null | undefined): string {
  const normalized = normalizeWorkSectionStatusKey(stockStatus);

  if (
    !normalized ||
    normalized === 'pendente' ||
    normalized === 'solicitado' ||
    normalized === 'requested' ||
    normalized === 'pending'
  ) {
    return 'Pendente';
  }

  if (
    normalized.includes('em separacao') ||
    normalized.includes('inseparation') ||
    normalized.includes('in separation') ||
    normalized === 'separacao' ||
    normalized === 'separation' ||
    normalized.includes('aguardando retirada') ||
    normalized.includes('aguardandoretirada') ||
    normalized.includes('awaiting pickup') ||
    normalized.includes('awaitingpickup') ||
    normalized.includes('ready for pickup') ||
    normalized.includes('readyforpickup') ||
    normalized.includes('atendido parcialmente') ||
    normalized.includes('partiallyfulfilled') ||
    normalized === 'partially fulfilled'
  ) {
    return 'Aguardando Peças';
  }

  if (
    normalized === 'atendido' ||
    normalized === 'fulfilled' ||
    normalized === 'requestfulfilled'
  ) {
    return 'Em Andamento';
  }

  if (
    normalized === 'cancelado' ||
    normalized === 'cancelled' ||
    normalized === 'rejeitado' ||
    normalized === 'rejected'
  ) {
    return 'Cancelado';
  }

  return 'Pendente';
}

export interface ServiceOrderWorkSectionTotals {
  serviceOrderId: number | null;
  sectionsCount: number;
  itemsCount: number;
  servicesCount: number;
  itemsTotal: number;
  servicesTotal: number;
  grandTotal: number;
}

export interface ServiceOrderWorkSectionItem {
  id: number;
  serviceOrderWorkSectionId: number | null;
  itemId: number | null;
  description: string;
  quantity: number;
  unitValue: number;
  totalValue: number;
  /** Preventiva | Corretiva. */
  maintenanceKind?: string | null;
  /** % cobrado a mais na corretiva. */
  chargePercent?: number | null;
  paymentStatus: string | null;
  hasFault: boolean;
  withdrawnFromStock: boolean;
  inStock: boolean;
  approvalStatus?: string | null;
  rejectionReason?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ServiceOrderWorkSectionItemCreate {
  serviceOrderWorkSectionId: number;
  itemId: number | null;
  description: string;
  quantity: number;
  unitValue: number;
  maintenanceKind?: string;
  chargePercent?: number | null;
  paymentStatus?: string;
  hasFault?: boolean;
  withdrawnFromStock?: boolean;
  inStock?: boolean;
}

export interface ServiceOrderWorkSectionItemUpdate {
  serviceOrderWorkSectionId?: number;
  itemId?: number;
  description?: string;
  quantity?: number;
  unitValue?: number;
  paymentStatus?: string;
  hasFault?: boolean;
  withdrawnFromStock?: boolean;
  inStock?: boolean;
}

export interface ServiceOrderWorkSectionService {
  id: number;
  serviceOrderWorkSectionId: number | null;
  serviceTypeId: number | null;
  externalServiceProviderId?: number | null;
  externalServiceProviderName?: string | null;
  description: string;
  status?: string | null;
  quantity: number;
  unitValue: number;
  totalValue: number;
  /** Preventiva | Corretiva. */
  maintenanceKind?: string | null;
  /** % cobrado a mais na corretiva. */
  chargePercent?: number | null;
  paymentStatus: string | null;
  hasFault: boolean;
  isGuincho?: boolean;
  approvalStatus?: string | null;
  rejectionReason?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ServiceOrderWorkSectionServiceCreate {
  serviceOrderWorkSectionId: number;
  serviceTypeId: number | null;
  externalServiceProviderId?: number | null;
  description: string;
  quantity: number;
  unitValue: number;
  maintenanceKind?: string;
  chargePercent?: number | null;
  paymentStatus?: string;
  hasFault?: boolean;
  isGuincho?: boolean;
}

/** Ajuste pontual de cobrança da linha: avaria e/ou status de pagamento. */
export interface ServiceOrderWorkSectionLineBilling {
  hasFault?: boolean;
  paymentStatus?: string;
}

export interface ServiceOrderWorkSectionServiceUpdate {
  serviceOrderWorkSectionId?: number;
  serviceTypeId?: number;
  externalServiceProviderId?: number | null;
  description?: string;
  quantity?: number;
  unitValue?: number;
  paymentStatus?: string;
  hasFault?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ServiceOrderWorkSectionsService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'serviceorderworksections';
  private readonly itemsResourcePath = 'serviceorderworksectionitems';
  private readonly servicesResourcePath = 'serviceorderworksectionservices';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;

    if (baseQuery) {
      url += `?${baseQuery}`;
    }

    return url;
  }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headerMap);
  }

  getAll(): Observable<ServiceOrderWorkSection[]> {
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders() }).pipe(
      map(response => this.extractArray(response).map(item => this.mapSectionFromDto(item))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar seções de trabalho da ordem de serviço');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<ServiceOrderWorkSection> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(response => this.mapSectionFromDto(this.extractItem(response))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar seção de trabalho da ordem de serviço');
        return throwError(() => err);
      })
    );
  }

  getByServiceOrderId(serviceOrderId: number): Observable<ServiceOrderWorkSection[]> {
    const params = new HttpParams().set('serviceOrderId', String(serviceOrderId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(response => this.extractArray(response).map(item => this.mapSectionFromDto(item))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar seções por ordem de serviço');
        return throwError(() => err);
      })
    );
  }

  getTotalsByServiceOrderId(serviceOrderId: number): Observable<ServiceOrderWorkSectionTotals> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/serviceorder/${serviceOrderId}/totals`), { headers: this.getHeaders() }).pipe(
      map(response => this.mapTotalsFromDto(this.extractItem(response), serviceOrderId)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar totais das seções da ordem de serviço');
        return throwError(() => err);
      })
    );
  }

  create(payload: ServiceOrderWorkSectionCreate, showSuccessMessage: boolean = true): Observable<ServiceOrderWorkSection> {
    return this.http.post<any>(this.buildUrl(this.resourcePath), payload, { headers: this.getHeaders() }).pipe(
      map(response => this.mapSectionFromDto(this.extractItem(response))),
      tap(() => {
        if (showSuccessMessage) {
          this.errorService.showSuccessMessage('Seção de trabalho criada com sucesso');
        }
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar seção de trabalho da ordem de serviço');
        return throwError(() => err);
      })
    );
  }

  update(
    id: number,
    payload: ServiceOrderWorkSectionUpdate,
    showSuccessMessage: boolean = true
  ): Observable<ServiceOrderWorkSection> {
    return this.http.put<any>(this.buildUrl(`${this.resourcePath}/${id}`), payload, { headers: this.getHeaders() }).pipe(
      map(response => this.mapSectionFromDto(this.extractItem(response))),
      tap(() => {
        if (showSuccessMessage) {
          this.errorService.showSuccessMessage('Seção de trabalho atualizada com sucesso');
        }
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar seção de trabalho da ordem de serviço');
        return throwError(() => err);
      })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(() => void 0),
      tap(() => this.errorService.showSuccessMessage('Seção de trabalho removida com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover seção de trabalho da ordem de serviço');
        return throwError(() => err);
      })
    );
  }

  getAllItems(): Observable<ServiceOrderWorkSectionItem[]> {
    return this.http.get<any>(this.buildUrl(this.itemsResourcePath), { headers: this.getHeaders() }).pipe(
      map(response => this.extractArray(response).map(item => this.mapItemFromDto(item))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar itens das seções de trabalho');
        return throwError(() => err);
      })
    );
  }

  getItemById(id: number): Observable<ServiceOrderWorkSectionItem> {
    return this.http.get<any>(this.buildUrl(`${this.itemsResourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(response => this.mapItemFromDto(this.extractItem(response))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar item da seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  getItemsByWorkSectionId(workSectionId: number): Observable<ServiceOrderWorkSectionItem[]> {
    const params = new HttpParams().set('workSectionId', String(workSectionId));
    return this.http.get<any>(this.buildUrl(this.itemsResourcePath), { headers: this.getHeaders(), params }).pipe(
      map(response => this.extractArray(response).map(item => this.mapItemFromDto(item))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar itens por seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  getItemsByItemId(itemId: number): Observable<ServiceOrderWorkSectionItem[]> {
    const params = new HttpParams().set('itemId', String(itemId));
    return this.http.get<any>(this.buildUrl(this.itemsResourcePath), { headers: this.getHeaders(), params }).pipe(
      map(response => this.extractArray(response).map(item => this.mapItemFromDto(item))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar itens por item');
        return throwError(() => err);
      })
    );
  }

  createItem(payload: ServiceOrderWorkSectionItemCreate, showSuccessMessage: boolean = true): Observable<ServiceOrderWorkSectionItem> {
    return this.http.post<any>(this.buildUrl(this.itemsResourcePath), payload, { headers: this.getHeaders() }).pipe(
      map(response => this.mapItemFromDto(this.extractItem(response))),
      tap(() => {
        if (showSuccessMessage) {
          this.errorService.showSuccessMessage('Item da seção de trabalho criado com sucesso');
        }
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar item da seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  updateItem(
    id: number,
    payload: ServiceOrderWorkSectionItemUpdate,
    showSuccessMessage: boolean = true
  ): Observable<ServiceOrderWorkSectionItem> {
    return this.http.put<any>(this.buildUrl(`${this.itemsResourcePath}/${id}`), payload, { headers: this.getHeaders() }).pipe(
      map(response => this.mapItemFromDto(this.extractItem(response))),
      tap(() => {
        if (showSuccessMessage) {
          this.errorService.showSuccessMessage('Item da seção de trabalho atualizado com sucesso');
        }
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar item da seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  deleteItem(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.itemsResourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(() => void 0),
      tap(() => this.errorService.showSuccessMessage('Item da seção de trabalho removido com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover item da seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  getAllServices(): Observable<ServiceOrderWorkSectionService[]> {
    return this.http.get<any>(this.buildUrl(this.servicesResourcePath), { headers: this.getHeaders() }).pipe(
      map(response => this.extractArray(response).map(item => this.mapServiceFromDto(item))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar serviços das seções de trabalho');
        return throwError(() => err);
      })
    );
  }

  getServiceById(id: number): Observable<ServiceOrderWorkSectionService> {
    return this.http.get<any>(this.buildUrl(`${this.servicesResourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(response => this.mapServiceFromDto(this.extractItem(response))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar serviço da seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  getServicesByWorkSectionId(workSectionId: number): Observable<ServiceOrderWorkSectionService[]> {
    const params = new HttpParams().set('workSectionId', String(workSectionId));
    return this.http.get<any>(this.buildUrl(this.servicesResourcePath), { headers: this.getHeaders(), params }).pipe(
      map(response => this.extractArray(response).map(item => this.mapServiceFromDto(item))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar serviços por seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  getServicesByServiceTypeId(serviceTypeId: number): Observable<ServiceOrderWorkSectionService[]> {
    const params = new HttpParams().set('serviceTypeId', String(serviceTypeId));
    return this.http.get<any>(this.buildUrl(this.servicesResourcePath), { headers: this.getHeaders(), params }).pipe(
      map(response => this.extractArray(response).map(item => this.mapServiceFromDto(item))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar serviços por tipo de serviço');
        return throwError(() => err);
      })
    );
  }

  createService(payload: ServiceOrderWorkSectionServiceCreate, showSuccessMessage: boolean = true): Observable<ServiceOrderWorkSectionService> {
    return this.http.post<any>(this.buildUrl(this.servicesResourcePath), payload, { headers: this.getHeaders() }).pipe(
      map(response => this.mapServiceFromDto(this.extractItem(response))),
      tap(() => {
        if (showSuccessMessage) {
          this.errorService.showSuccessMessage('Serviço da seção de trabalho criado com sucesso');
        }
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar serviço da seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  updateService(
    id: number,
    payload: ServiceOrderWorkSectionServiceUpdate,
    showSuccessMessage: boolean = true
  ): Observable<ServiceOrderWorkSectionService> {
    return this.http.put<any>(this.buildUrl(`${this.servicesResourcePath}/${id}`), payload, { headers: this.getHeaders() }).pipe(
      map(response => this.mapServiceFromDto(this.extractItem(response))),
      tap(() => {
        if (showSuccessMessage) {
          this.errorService.showSuccessMessage('Serviço da seção de trabalho atualizado com sucesso');
        }
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar serviço da seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  /**
   * Ajusta avaria/status de pagamento de um item já salvo. Não use o PUT para isso:
   * ele sobrescreve a linha e apaga chargePercent, maintenanceKind e approvalStatus.
   */
  setItemBilling(
    id: number,
    payload: ServiceOrderWorkSectionLineBilling,
    showSuccessMessage: boolean = false
  ): Observable<ServiceOrderWorkSectionItem> {
    return this.http.patch<any>(this.buildUrl(`${this.itemsResourcePath}/${id}/billing`), payload, { headers: this.getHeaders() }).pipe(
      map(response => this.mapItemFromDto(this.extractItem(response))),
      tap(() => {
        if (showSuccessMessage) {
          this.errorService.showSuccessMessage('Avaria da peça atualizada com sucesso');
        }
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar a avaria da peça');
        return throwError(() => err);
      })
    );
  }

  /** Mesmo contrato do item, para linhas de serviço. */
  setServiceBilling(
    id: number,
    payload: ServiceOrderWorkSectionLineBilling,
    showSuccessMessage: boolean = false
  ): Observable<ServiceOrderWorkSectionService> {
    return this.http.patch<any>(this.buildUrl(`${this.servicesResourcePath}/${id}/billing`), payload, { headers: this.getHeaders() }).pipe(
      map(response => this.mapServiceFromDto(this.extractItem(response))),
      tap(() => {
        if (showSuccessMessage) {
          this.errorService.showSuccessMessage('Avaria do serviço atualizada com sucesso');
        }
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar a avaria do serviço');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza paymentStatus de itens e serviços de uma seção, preservando campos obrigatórios.
   */
  updateItemsPaymentStatus(
    workSectionId: number,
    paymentStatus: string,
    options?: {
      filterItem?: (item: ServiceOrderWorkSectionItem) => boolean;
      filterService?: (service: ServiceOrderWorkSectionService) => boolean;
      showSuccessMessage?: boolean;
    }
  ): Observable<void> {
    const showSuccessMessage = options?.showSuccessMessage ?? true;
    const filterItem = options?.filterItem ?? (() => true);
    const filterService = options?.filterService ?? (() => true);

    return forkJoin({
      items: this.getItemsByWorkSectionId(workSectionId).pipe(
        catchError(() => of([] as ServiceOrderWorkSectionItem[]))
      ),
      services: this.getServicesByWorkSectionId(workSectionId).pipe(
        catchError(() => of([] as ServiceOrderWorkSectionService[]))
      )
    }).pipe(
      switchMap(({ items, services }) => {
        const itemUpdates = (items || [])
          .filter(filterItem)
          .map(it =>
            this.setItemBilling(it.id, { paymentStatus }, showSuccessMessage).pipe(catchError(() => of(null)))
          );

        const serviceUpdates = (services || [])
          .filter(filterService)
          .map(sv =>
            this.setServiceBilling(sv.id, { paymentStatus }, showSuccessMessage).pipe(catchError(() => of(null)))
          );

        const all = [...itemUpdates, ...serviceUpdates];
        if (all.length === 0) {
          return of(void 0);
        }
        return forkJoin(all).pipe(map(() => void 0));
      }),
      catchError(() => of(void 0))
    );
  }

  deleteService(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.servicesResourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(() => void 0),
      tap(() => this.errorService.showSuccessMessage('Serviço da seção de trabalho removido com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover serviço da seção de trabalho');
        return throwError(() => err);
      })
    );
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    if (Array.isArray(response?.serviceOrderWorkSections)) return response.serviceOrderWorkSections;
    if (Array.isArray(response?.ServiceOrderWorkSections)) return response.ServiceOrderWorkSections;
    if (Array.isArray(response?.serviceOrderWorkSectionItems)) return response.serviceOrderWorkSectionItems;
    if (Array.isArray(response?.ServiceOrderWorkSectionItems)) return response.ServiceOrderWorkSectionItems;
    if (Array.isArray(response?.serviceOrderWorkSectionServices)) return response.serviceOrderWorkSectionServices;
    if (Array.isArray(response?.ServiceOrderWorkSectionServices)) return response.ServiceOrderWorkSectionServices;
    return [];
  }

  private extractItem(response: any): any {
    return response?.data?.data
      ?? response?.data
      ?? response?.item
      ?? response?.serviceOrderWorkSection
      ?? response?.serviceOrderWorkSectionItem
      ?? response?.serviceOrderWorkSectionService
      ?? response;
  }

  private mapSectionFromDto(dto: any): ServiceOrderWorkSection {
    const providerDto = dto?.ExternalServiceProvider ?? dto?.externalServiceProvider ?? null;
    const externalServiceProvider = providerDto
      ? {
          id: this.toNumber(providerDto?.Id ?? providerDto?.id),
          tradeName: providerDto?.TradeName ?? providerDto?.tradeName ?? null,
          companyName: providerDto?.CompanyName ?? providerDto?.companyName ?? null,
          cnpj: providerDto?.Cnpj ?? providerDto?.cnpj ?? null,
          phone: providerDto?.Phone ?? providerDto?.phone ?? null,
          contactEmail: providerDto?.ContactEmail ?? providerDto?.contactEmail ?? null,
          responsibleName: providerDto?.ResponsibleName ?? providerDto?.responsibleName ?? null,
          serviceType: providerDto?.ServiceType ?? providerDto?.serviceType ?? null,
        }
      : null;

    return {
      id: this.toNumber(dto?.Id ?? dto?.id) ?? 0,
      serviceOrderId: this.toNumber(dto?.ServiceOrderId ?? dto?.serviceOrderId),
      description: dto?.Description ?? dto?.description ?? '',
      status: dto?.Status ?? dto?.status ?? null,
      sourceBudgetWorkSectionId: this.toNumber(dto?.SourceBudgetWorkSectionId ?? dto?.sourceBudgetWorkSectionId),
      totalApproved: this.toNumber(dto?.TotalApproved ?? dto?.totalApproved),
      approved: dto?.Approved ?? dto?.approved ?? null,
      approvedBy: dto?.ApprovedBy ?? dto?.approvedBy ?? null,
      externalServiceProviderId: this.toNumber(dto?.ExternalServiceProviderId ?? dto?.externalServiceProviderId) ?? externalServiceProvider?.id ?? null,
      serviceOrderStockRequestsId: this.toNumber(
        dto?.ServiceOrderStockRequestsID ?? dto?.serviceOrderStockRequestsId ?? dto?.ServiceOrderStockRequestId
      ),
      externalServiceProvider,
      createdAt: dto?.CreatedAt ?? dto?.createdAt ?? null,
      updatedAt: dto?.UpdatedAt ?? dto?.updatedAt ?? null,
      items: Array.isArray(dto?.Items ?? dto?.items)
        ? (dto.Items ?? dto.items).map((item: any) => this.mapItemFromDto(item))
        : undefined,
      services: Array.isArray(dto?.Services ?? dto?.services)
        ? (dto.Services ?? dto.services).map((item: any) => this.mapServiceFromDto(item))
        : undefined
    };
  }

  private mapTotalsFromDto(dto: any, serviceOrderId: number): ServiceOrderWorkSectionTotals {
    return {
      serviceOrderId: this.toNumber(dto?.ServiceOrderId ?? dto?.serviceOrderId) ?? serviceOrderId,
      sectionsCount: this.toNumber(dto?.SectionsCount ?? dto?.sectionsCount ?? dto?.TotalSections ?? dto?.totalSections) ?? 0,
      itemsCount: this.toNumber(dto?.ItemsCount ?? dto?.itemsCount ?? dto?.TotalItems ?? dto?.totalItems) ?? 0,
      servicesCount: this.toNumber(dto?.ServicesCount ?? dto?.servicesCount ?? dto?.TotalServices ?? dto?.totalServices) ?? 0,
      itemsTotal: this.toNumber(dto?.ItemsTotal ?? dto?.itemsTotal ?? dto?.TotalItemsValue ?? dto?.totalItemsValue) ?? 0,
      servicesTotal: this.toNumber(dto?.ServicesTotal ?? dto?.servicesTotal ?? dto?.TotalServicesValue ?? dto?.totalServicesValue) ?? 0,
      grandTotal: this.toNumber(dto?.GrandTotal ?? dto?.grandTotal ?? dto?.Total ?? dto?.total) ?? 0,
    };
  }

  private mapItemFromDto(dto: any): ServiceOrderWorkSectionItem {
    const quantity = this.toNumber(dto?.Quantity ?? dto?.quantity) ?? 0;
    const unitValue = this.toNumber(dto?.UnitValue ?? dto?.unitValue) ?? 0;
    const totalValue = this.toNumber(
      dto?.Total ?? dto?.total ?? dto?.TotalValue ?? dto?.totalValue
    ) ?? (quantity * unitValue);
    const chargePercent = this.toNumber(dto?.ChargePercent ?? dto?.chargePercent);

    return {
      id: this.toNumber(dto?.Id ?? dto?.id) ?? 0,
      serviceOrderWorkSectionId: this.toNumber(dto?.ServiceOrderWorkSectionId ?? dto?.serviceOrderWorkSectionId),
      itemId: this.toNumber(dto?.ItemId ?? dto?.itemId),
      description: dto?.Description ?? dto?.description ?? '',
      quantity,
      unitValue,
      totalValue,
      maintenanceKind: this.normalizeMaintenanceKind(dto?.MaintenanceKind ?? dto?.maintenanceKind),
      chargePercent,
      paymentStatus: dto?.PaymentStatus ?? dto?.paymentStatus ?? null,
      hasFault: Boolean(dto?.HasFault ?? dto?.hasFault ?? false),
      withdrawnFromStock: Boolean(dto?.WithdrawnFromStock ?? dto?.withdrawnFromStock ?? false),
      inStock: Boolean(dto?.InStock ?? dto?.inStock ?? false),
      approvalStatus: dto?.ApprovalStatus ?? dto?.approvalStatus ?? null,
      rejectionReason: dto?.RejectionReason ?? dto?.rejectionReason ?? null,
      createdAt: dto?.CreatedAt ?? dto?.createdAt ?? null,
      updatedAt: dto?.UpdatedAt ?? dto?.updatedAt ?? null,
    };
  }

  private mapServiceFromDto(dto: any): ServiceOrderWorkSectionService {
    const quantity = this.toNumber(dto?.Quantity ?? dto?.quantity) ?? 0;
    const unitValue = this.toNumber(dto?.UnitValue ?? dto?.unitValue) ?? 0;
    const totalValue = this.toNumber(
      dto?.Total ?? dto?.total ?? dto?.TotalValue ?? dto?.totalValue
    ) ?? (quantity * unitValue);
    const chargePercent = this.toNumber(dto?.ChargePercent ?? dto?.chargePercent);

    return {
      id: this.toNumber(dto?.Id ?? dto?.id) ?? 0,
      serviceOrderWorkSectionId: this.toNumber(dto?.ServiceOrderWorkSectionId ?? dto?.serviceOrderWorkSectionId),
      serviceTypeId: this.toNumber(dto?.ServiceTypeId ?? dto?.serviceTypeId),
      externalServiceProviderId: this.toNumber(dto?.ExternalServiceProviderId ?? dto?.externalServiceProviderId),
      externalServiceProviderName:
        dto?.ExternalServiceProviderName
        ?? dto?.externalServiceProviderName
        ?? dto?.ExternalServiceProvider?.TradeName
        ?? dto?.externalServiceProvider?.tradeName
        ?? dto?.ExternalServiceProvider?.CompanyName
        ?? dto?.externalServiceProvider?.companyName
        ?? null,
      description: dto?.Description ?? dto?.description ?? '',
      status: dto?.Status ?? dto?.status ?? null,
      quantity,
      unitValue,
      totalValue,
      maintenanceKind: this.normalizeMaintenanceKind(dto?.MaintenanceKind ?? dto?.maintenanceKind),
      chargePercent,
      paymentStatus: dto?.PaymentStatus ?? dto?.paymentStatus ?? null,
      hasFault: Boolean(dto?.HasFault ?? dto?.hasFault ?? false),
      isGuincho: Boolean(dto?.IsGuincho ?? dto?.isGuincho ?? false),
      approvalStatus: dto?.ApprovalStatus ?? dto?.approvalStatus ?? null,
      rejectionReason: dto?.RejectionReason ?? dto?.rejectionReason ?? null,
      createdAt: dto?.CreatedAt ?? dto?.createdAt ?? null,
      updatedAt: dto?.UpdatedAt ?? dto?.updatedAt ?? null,
    };
  }

  private normalizeMaintenanceKind(value: unknown): string {
    const s = String(value ?? '').trim().toLowerCase();
    if (s.startsWith('prevent')) return 'Preventiva';
    return 'Corretiva';
  }

  private toNumber(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}